import * as TabsPrimitive from "@radix-ui/react-tabs"
import type { ComponentProps } from "react"
import { cn } from "@/lib/utils.js"

/**
 * One choice out of a few, as pills on the muted fill.
 *
 * Radix rather than a row of buttons: a tab strip owes the reader arrow-key
 * navigation and a panel wired to the selected tab, and half of that contract
 * is worse than none.
 *
 * The configuration screen's country selector used to be the hand-rolled twin
 * of this, and the `[TBD]` here said adopting Radix would mean restructuring
 * that screen rather than swapping a component. That is exactly what happened:
 * the country strip is now a real tab list whose panel is one country's rule
 * set, so the panel has one segmented control and this is it.
 */
export const Tabs = TabsPrimitive.Root

export function TabsList({ className, ...props }: ComponentProps<typeof TabsPrimitive.List>) {
  return (
    <TabsPrimitive.List
      className={cn("inline-flex flex-wrap gap-0.5 rounded-lg bg-muted p-0.5", className)}
      {...props}
    />
  )
}

export function TabsTrigger({ className, ...props }: ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      className={cn(
        "flex h-8 items-center gap-2 rounded-md px-3 text-[13px] text-muted-foreground transition-colors hover:text-foreground",
        // Only things that hover cast more than one step, so the selected pill
        // lifts off the fill with the same shadow every surface uses.
        "data-[state=active]:bg-card data-[state=active]:font-medium data-[state=active]:text-foreground data-[state=active]:shadow-xs",
        className,
      )}
      {...props}
    />
  )
}

export function TabsContent({ className, ...props }: ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content className={cn("focus-visible:outline-none", className)} {...props} />
  )
}
